/** Обёртка над Telegram WebApp SDK.
 *
 * Вне Telegram (обычный браузер при разработке) объекта `window.Telegram`
 * нет, и все функции здесь становятся безвредными заглушками. Благодаря
 * этому остальной код не обвешан проверками «а мы точно в Telegram?».
 */

import { getSettings } from '../game/settings'

export type HapticStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'
export type NotificationType = 'error' | 'success' | 'warning'

interface TelegramWebApp {
  initData: string
  colorScheme: 'light' | 'dark'
  themeParams: Record<string, string>
  ready(): void
  expand(): void
  openTelegramLink?(url: string): void
  setHeaderColor?(color: string): void
  onEvent(event: string, handler: () => void): void
  offEvent(event: string, handler: () => void): void
  BackButton: {
    show(): void
    hide(): void
    onClick(handler: () => void): void
    offClick(handler: () => void): void
  }
  HapticFeedback?: {
    impactOccurred(style: HapticStyle): void
    notificationOccurred(type: NotificationType): void
    selectionChanged(): void
  }
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp }
  }
}

export function getWebApp(): TelegramWebApp | undefined {
  return window.Telegram?.WebApp
}

/** Запущены ли мы действительно внутри Telegram. */
export function isInsideTelegram(): boolean {
  const webApp = getWebApp()
  return Boolean(webApp && webApp.initData)
}

/**
 * Перенести палитру Telegram в CSS-переменные.
 *
 * Mini App должен выглядеть частью мессенджера, а не чужой страницей,
 * поэтому цвета берутся из темы пользователя, а собственные значения
 * в CSS остаются лишь запасным вариантом.
 */
export function applyTelegramTheme(): void {
  const webApp = getWebApp()
  if (!webApp) return

  const root = document.documentElement
  for (const [key, value] of Object.entries(webApp.themeParams ?? {})) {
    root.style.setProperty(`--tg-${key.replace(/_/g, '-')}`, value)
  }
  root.dataset.theme = webApp.colorScheme
}

export function initTelegram(): void {
  const webApp = getWebApp()
  if (!webApp) return

  webApp.ready()
  webApp.expand()
  applyTelegramTheme()
  webApp.onEvent('themeChanged', applyTelegramTheme)
}

/**
 * Применить купленное оформление доски.
 *
 * Ставится атрибутом на корень документа, а не классом на компоненте:
 * доска встречается на нескольких экранах (игра, витрина), и переключать
 * их по отдельности значит рано или поздно забыть один.
 */
export function applyBoardTheme(code: string): void {
  document.documentElement.dataset.boardTheme = code || 'classic'
}

/**
 * Открыть окно «поделиться» с готовым текстом и ссылкой.
 *
 * Внутри Telegram это делает сам клиент, снаружи (при отладке в браузере)
 * остаётся обычная вкладка. Ссылка всегда реферальная: делиться результатом
 * и звать друга — одно и то же действие, разделять их незачем.
 */
export function shareToChat(url: string, text: string): void {
  const target = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
  const webApp = getWebApp()
  if (webApp?.openTelegramLink) {
    webApp.openTelegramLink(target)
  } else {
    window.open(target, '_blank', 'noopener')
  }
}

/** Вибрация. Уважает настройку игрока: кого-то она раздражает сильнее звука. */
export const haptic = {
  tap(style: HapticStyle = 'light') {
    if (!getSettings().haptics) return
    getWebApp()?.HapticFeedback?.impactOccurred(style)
  },
  select() {
    if (!getSettings().haptics) return
    getWebApp()?.HapticFeedback?.selectionChanged()
  },
  notify(type: NotificationType) {
    if (!getSettings().haptics) return
    getWebApp()?.HapticFeedback?.notificationOccurred(type)
  },
}

export const backButton = {
  show(handler: () => void) {
    const button = getWebApp()?.BackButton
    if (!button) return () => {}
    button.onClick(handler)
    button.show()
    return () => {
      button.offClick(handler)
      button.hide()
    }
  },
}
